import React, { useCallback, useEffect, useMemo, useState } from 'react';

import {
  CoberturaEspecialidades,
  Especialidade,
  ImpactoEspecialidade,
  createEspecialidade,
  deleteEspecialidade,
  descreverImpacto,
  getCoberturaEspecialidades,
  getEspecialidades,
  updateEspecialidade,
} from '../../../api/especialidadesApi';
import { fetchCollaborators } from '../../../api/collaboratorsApi';
import { useEsteiras } from '../../../hooks/useEsteiras';
import { notify } from '../../../shared/feedback/notify';
import ComoFunciona, { ComoFuncionaSecao } from '../../../components/como-funciona/ComoFunciona';
import ConfirmModal from '../../../components/modal/confirm/ConfirmModal';
import CollaboratorModal, {
  ColaboradorSelecionado,
} from '../../../components/modal/collaborator/CollaboratorModal';
import '../../../components/como-funciona/help-circle-btn.css';
// Esqueleto de cabeçalho compartilhado (`recepcao-header-row`, `recepcao-title`). O import é
// explícito de propósito: contar com o CSS que a página hospedeira carrega é acoplamento
// invisível — a aba quebraria em silêncio se fosse montada em outra tela (lição do BUG-V74).
import '../../recepcao/pages/Recepcao.css';

const helpSections: ComoFuncionaSecao[] = [
  {
    titulo: 'O que é uma especialidade',
    conteudo: (
      <>
        <p>
          Uma <strong>especialidade</strong> descreve o que um colaborador sabe fazer — Pintor,
          Funileiro, Mecânico, o que a operação precisar. Cada especialidade aponta{' '}
          <strong>em quais esteiras</strong> ela atua e <strong>quais colaboradores</strong> a
          possuem.
        </p>
        <p>
          Um colaborador pode ter várias especialidades: nesse caso ele atende a{' '}
          <strong>soma</strong> das esteiras de todas elas.
        </p>
      </>
    ),
  },
  {
    titulo: 'O que a regra bloqueia',
    conteudo: (
      <>
        <p>
          Com a regra valendo, um colaborador só pode ser <strong>adicionado a um veículo</strong> e{' '}
          <strong>iniciar atividade</strong> em esteiras das suas especialidades. Quem não tem
          nenhuma especialidade não pode ser adicionado a veículo nenhum, e esteira que nenhuma
          especialidade cobre não recebe colaborador — os dois casos aparecem no aviso de cobertura
          no topo desta aba.
        </p>
        <p>
          As etapas do próprio sistema (Aguardando, Orçamento, Aguardando Entrega, Entrega) não
          dependem de especialidade e seguem funcionando normalmente. A{' '}
          <strong>liberação da administração</strong> continua valendo em paralelo: o veículo precisa
          das duas coisas para ser trabalhado.
        </p>
      </>
    ),
  },
  {
    titulo: 'Enquanto não houver nenhuma especialidade',
    conteudo: (
      <p>
        A restrição só passa a valer quando existe <strong>ao menos uma especialidade ativa</strong>.
        Antes disso, tudo funciona como antes e qualquer colaborador pode ser adicionado a qualquer
        esteira. É isso que permite configurar com calma, sem parar a oficina — e é por isso que a
        primeira especialidade criada muda o comportamento para todos.
      </p>
    ),
  },
  {
    titulo: 'Criar não tira ninguém do veículo',
    conteudo: (
      <p>
        Ao criar a primeira especialidade, quem já está em um veículo <strong>continua nele</strong>,
        mesmo que a esteira não seja da especialidade dele — retirar todos de uma vez pararia a
        oficina no meio da configuração. Esses casos ficam listados como{' '}
        <strong>vínculos a regularizar</strong> no aviso de cobertura, e você decide quando ajustar.
        A partir daí, toda nova atribuição e todo início de atividade já seguem a regra.
      </p>
    ),
  },
  {
    titulo: 'Editar ou excluir com gente trabalhando',
    conteudo: (
      <p>
        Se a alteração tirar de alguém a esteira em que ele está trabalhando, o sistema{' '}
        <strong>avisa antes</strong> e lista quem será afetado. Ao confirmar, essas pessoas são
        retiradas dos veículos e as atividades em aberto são encerradas — as{' '}
        <strong>horas trabalhadas não são perdidas</strong>: vão para a aprovação do Chefe de
        Oficina, como em qualquer encerramento. <strong>Desativar</strong> uma especialidade guarda a
        configuração e a tira da regra; <strong>excluir</strong> apaga de vez.
      </p>
    ),
  },
  {
    titulo: 'Quando o veículo muda de esteira',
    conteudo: (
      <p>
        Ao mover um veículo para uma esteira que não é da especialidade de quem está nele, o Chefe de
        Oficina recebe o mesmo aviso e decide. Confirmando, essas pessoas saem do veículo (com as
        horas indo para aprovação) e quem atende a esteira nova permanece.
      </p>
    ),
  },
  {
    titulo: 'O mecânico que trabalha num veículo de outra etapa',
    conteudo: (
      <>
        <p>
          A regra <strong>não tem exceção</strong>: cada pessoa só trabalha nas esteiras da
          especialidade dela. O caso do mecânico que precisa atuar num veículo parado em
          Desmontagem é resolvido de outro jeito — o Chefe de Oficina abre{' '}
          <strong>Mecânica em paralelo</strong> naquele veículo, e o mecânico aponta as horas em
          Mecânica, que é a esteira dele.
        </p>
        <p>
          Por isso, quem tem serviço aberto em paralelo <strong>não aparece</strong> na lista de
          vínculos a regularizar acima: o vínculo dele está correto, e a esteira que ele atende é
          uma das que estão em curso no veículo.
        </p>
      </>
    ),
  },
];

const EspecialidadesSection: React.FC = () => {
  const { data: esteiras = [] } = useEsteiras();
  const [especialidades, setEspecialidades] = useState<Especialidade[]>([]);
  const [regraAtiva, setRegraAtiva] = useState(false);
  const [cobertura, setCobertura] = useState<CoberturaEspecialidades | null>(null);
  const [carregando, setCarregando] = useState(true);
  /**
   * at-60: a carga falhou. Sem este flag a tela mostraria "nenhuma especialidade cadastrada" e o
   * aviso de regra desligada — duas AFIRMAÇÕES de negócio que a consulta não sustenta.
   */
  const [cargaFalhou, setCargaFalhou] = useState(false);
  const [isHelpOpen, setIsHelpOpen] = useState(false);
  const [salvando, setSalvando] = useState(false);

  // Formulário — serve para criar e para editar (quando `editandoId` está preenchido).
  const [editandoId, setEditandoId] = useState<string | null>(null);
  const [nome, setNome] = useState('');
  const [esteiraIds, setEsteiraIds] = useState<string[]>([]);
  const [colaboradores, setColaboradores] = useState<ColaboradorSelecionado[]>([]);
  const [ativa, setAtiva] = useState(true);

  // Seleção de colaboradores pelo modal paginado compartilhado (mesmo das telas de veículo).
  const [colaboradoresPagina, setColaboradoresPagina] = useState<any[]>([]);
  const [totalColaboradores, setTotalColaboradores] = useState(0);
  const [isColaboradoresModalOpen, setIsColaboradoresModalOpen] = useState(false);
  const [selecaoNoModal, setSelecaoNoModal] = useState<ColaboradorSelecionado[]>([]);

  const [paraExcluir, setParaExcluir] = useState<Especialidade | null>(null);
  /** Confirmação do impacto: descreve o que vai acontecer e guarda a ação que reenvia com aceite. */
  const [confirmacaoImpacto, setConfirmacaoImpacto] = useState<{
    mensagem: string;
    onConfirm: () => void;
  } | null>(null);

  /** Só esteiras de TRABALHO ativas: as de sistema não dependem de especialidade. */
  const esteirasSelecionaveis = useMemo(
    () => esteiras.filter((e: any) => e.ativo && !e.sistema),
    [esteiras],
  );

  /**
   * `useCallback` sem dependências é exigência do `CollaboratorModal`: ele dispara a carga num
   * efeito que observa a identidade desta função (documentado no próprio modal).
   */
  const carregarPaginaDeColaboradores = useCallback(async (pagina: number) => {
    try {
      const { users, total } = await fetchCollaborators(pagina);
      setColaboradoresPagina(users);
      setTotalColaboradores(total);
    } catch (erro) {
      notify.errorFrom(
        erro,
        'Não foi possível carregar os colaboradores. Recarregue a página; se persistir, acione a TI.',
      );
    }
  }, []);

  const carregar = useCallback(async () => {
    setCarregando(true);
    try {
      const [lista, coberturaApi] = await Promise.all([
        getEspecialidades(),
        getCoberturaEspecialidades(),
      ]);
      setEspecialidades(lista.especialidades);
      setRegraAtiva(lista.regraAtiva);
      setCobertura(coberturaApi);
      setCargaFalhou(false);
    } catch (erro) {
      // Estado seguro + sinal visível (at-60). O `cargaFalhou` impede que a tela vazia seja lida
      // como "não há especialidades".
      setEspecialidades([]);
      setCobertura(null);
      setCargaFalhou(true);
      notify.errorFrom(
        erro,
        'Não foi possível carregar as especialidades. Recarregue a página; se persistir, acione a TI.',
      );
    } finally {
      setCarregando(false);
    }
  }, []);

  useEffect(() => {
    carregar();
  }, [carregar]);

  const limparFormulario = () => {
    setEditandoId(null);
    setNome('');
    setEsteiraIds([]);
    setColaboradores([]);
    setAtiva(true);
  };

  const editar = (especialidade: Especialidade) => {
    setEditandoId(especialidade.id);
    setNome(especialidade.nome);
    setEsteiraIds(especialidade.esteiras.map((e) => e.id));
    setColaboradores(
      especialidade.colaboradores.map((c) => ({ id: c.id, fullName: c.nome })),
    );
    setAtiva(especialidade.ativa);
  };

  const alternarEsteira = (id: string) => {
    setEsteiraIds((atual) =>
      atual.includes(id) ? atual.filter((e) => e !== id) : [...atual, id],
    );
  };

  const montarMensagemDeImpacto = (impacto: ImpactoEspecialidade[], mensagemDoServidor?: string) => {
    const linhas = impacto.map((item) => `- ${descreverImpacto(item)}`).join('\n');
    return `${mensagemDoServidor ?? 'Esta alteração afeta trabalho em andamento.'}\n\n${linhas}\n\nDeseja continuar?`;
  };

  /** Extrai o impacto do 409; devolve null quando o erro é de outra natureza. */
  const impactoDoErro = (erro: any): { impacto: ImpactoEspecialidade[]; message?: string } | null => {
    const data = erro?.response?.data;
    if (erro?.response?.status === 409 && data?.code === 'ESPECIALIDADE_IMPACTA_TRABALHO') {
      return { impacto: data.impacto ?? [], message: data.message };
    }
    return null;
  };

  const avisarAfetados = (afetados: ImpactoEspecialidade[]) => {
    if (!afetados.length) return;
    notify.warning(
      `${afetados.length === 1 ? '1 colaborador foi retirado' : `${afetados.length} colaboradores foram retirados`} ` +
        'do veículo por não ter a especialidade da esteira. As horas em aberto foram enviadas para aprovação.',
    );
  };

  const salvar = async (confirmar = false) => {
    const nomeLimpo = nome.trim();
    if (!nomeLimpo) {
      notify.warning('Informe o nome da especialidade.');
      return;
    }

    setSalvando(true);
    try {
      const payload = {
        nome: nomeLimpo,
        esteiraIds,
        colaboradorIds: colaboradores.map((c) => c.id),
        ativa,
      };

      const salva = editandoId
        ? await updateEspecialidade(editandoId, { ...payload, confirmar })
        : await createEspecialidade(payload);

      // at-65 (BUG-V51): `actionSuccess` conjuga sempre no masculino singular, então
      // "Especialidade X" saía como "criado/atualizado". Frase montada aqui, com a
      // concordância certa.
      notify.success(
        editandoId
          ? `Especialidade "${nomeLimpo}" atualizada com sucesso.`
          : `Especialidade "${nomeLimpo}" criada com sucesso.`,
      );
      avisarAfetados(salva.afetados ?? []);

      // Criar a primeira especialidade LIGA a regra para a oficina inteira — o admin precisa saber
      // disso no momento em que acontece, não só pela hint.
      if (!editandoId && !regraAtiva) {
        notify.info(
          'A restrição por especialidade passou a valer: a partir de agora, colaborador sem a ' +
            'especialidade da esteira não pode ser adicionado ao veículo nem iniciar atividade.',
        );
      }

      limparFormulario();
      await carregar();
    } catch (erro: any) {
      const comImpacto = impactoDoErro(erro);
      if (comImpacto) {
        // Nada foi gravado: a tela descreve o efeito e só então reenvia com o aceite (at-18).
        setConfirmacaoImpacto({
          mensagem: montarMensagemDeImpacto(comImpacto.impacto, comImpacto.message),
          onConfirm: () => {
            setConfirmacaoImpacto(null);
            salvar(true);
          },
        });
        return;
      }
      // Nunca um falso sucesso: se falhou, o toast é de erro e o formulário permanece preenchido.
      notify.actionError(erro, {
        action: editandoId ? 'update' : 'create',
        subject: 'a especialidade',
      });
    } finally {
      setSalvando(false);
    }
  };

  const alternarAtiva = async (especialidade: Especialidade, confirmar = false) => {
    try {
      const salva = await updateEspecialidade(especialidade.id, {
        ativa: !especialidade.ativa,
        confirmar,
      });
      notify.success(
        especialidade.ativa
          ? `Especialidade "${especialidade.nome}" desativada.`
          : `Especialidade "${especialidade.nome}" reativada.`,
      );
      avisarAfetados(salva.afetados ?? []);
      await carregar();
    } catch (erro: any) {
      const comImpacto = impactoDoErro(erro);
      if (comImpacto) {
        setConfirmacaoImpacto({
          mensagem: montarMensagemDeImpacto(comImpacto.impacto, comImpacto.message),
          onConfirm: () => {
            setConfirmacaoImpacto(null);
            alternarAtiva(especialidade, true);
          },
        });
        return;
      }
      notify.actionError(erro, { action: 'update', subject: 'a especialidade' });
    }
  };

  const excluir = async (especialidade: Especialidade, confirmar = false) => {
    try {
      const resposta = await deleteEspecialidade(especialidade.id, confirmar);
      notify.success(`Especialidade "${especialidade.nome}" removida com sucesso.`);
      avisarAfetados(resposta.afetados ?? []);
      if (editandoId === especialidade.id) limparFormulario();
      await carregar();
    } catch (erro: any) {
      const comImpacto = impactoDoErro(erro);
      if (comImpacto) {
        setConfirmacaoImpacto({
          mensagem: montarMensagemDeImpacto(comImpacto.impacto, comImpacto.message),
          onConfirm: () => {
            setConfirmacaoImpacto(null);
            excluir(especialidade, true);
          },
        });
        return;
      }
      notify.actionError(erro, { action: 'delete', subject: 'a especialidade' });
    } finally {
      setParaExcluir(null);
    }
  };

  const temAvisoDeCobertura =
    !!cobertura?.regraAtiva &&
    (cobertura.esteirasSemCobertura.length > 0 ||
      cobertura.colaboradoresSemEspecialidade.length > 0 ||
      cobertura.vinculosInconsistentes.length > 0);

  const textoSecundario = { fontSize: '12px', color: '#6b7280' } as const;

  return (
    <>
      <div className="admin-aba-cabecalho">
        <div className="admin-card-title">
          Especialidades
          <button
            type="button"
            className="help-circle-btn"
            title="Como Funciona"
            onClick={() => setIsHelpOpen(true)}
          >
            ?
          </button>
        </div>
      </div>

      {/* at-60: a falha de carga não pode virar "não existe especialidade cadastrada" — a tela
          vazia seria indistinguível de configuração vazia, e escrever por cima dela é destrutivo. */}
      {cargaFalhou && (
        <div className="admin-card" style={{ borderLeft: '4px solid #dc2626' }}>
          <div className="admin-card-title">Configuração não carregada</div>
          <p style={{ fontSize: '13px', margin: 0 }}>
            Não foi possível carregar as especialidades, então esta aba não está mostrando a
            configuração real. Recarregue a página antes de criar ou alterar qualquer coisa — se
            persistir, acione a TI.
          </p>
        </div>
      )}

      {!cargaFalhou && !carregando && !regraAtiva && (
        <div className="admin-card" style={{ borderLeft: '4px solid #2563eb' }}>
          <div className="admin-card-title">Restrição desligada</div>
          <p style={{ fontSize: '13px', margin: 0 }}>
            Nenhuma especialidade ativa cadastrada: a oficina funciona como antes e qualquer
            colaborador pode ser adicionado a qualquer esteira. A restrição passa a valer assim que a{' '}
            <strong>primeira especialidade</strong> for criada.
          </p>
        </div>
      )}

      {/* Aviso de cobertura (pedido do Victor, 07/08/2026): sem ele, o esquecimento de configuração
          só apareceria na hora de atribuir alguém — quando a oficina já está esperando. */}
      {temAvisoDeCobertura && cobertura && (
        <div className="admin-card" style={{ borderLeft: '4px solid #d97706' }}>
          <div className="admin-card-title">Pontos de atenção na configuração</div>

          {cobertura.esteirasSemCobertura.length > 0 && (
            <p style={{ fontSize: '13px', margin: '4px 0' }}>
              <strong>Ninguém pode trabalhar nestas esteiras</strong> (nenhuma especialidade ativa as
              inclui): {cobertura.esteirasSemCobertura.map((e) => e.nome).join(', ')}.
            </p>
          )}

          {cobertura.colaboradoresSemEspecialidade.length > 0 && (
            <p style={{ fontSize: '13px', margin: '4px 0' }}>
              <strong>Estes colaboradores não podem ser adicionados a nenhum veículo</strong> (estão
              fora de toda especialidade ativa):{' '}
              {cobertura.colaboradoresSemEspecialidade.map((c) => c.nome).join(', ')}.
            </p>
          )}

          {cobertura.vinculosInconsistentes.length > 0 && (
            <>
              <p style={{ fontSize: '13px', margin: '4px 0' }}>
                <strong>Vínculos a regularizar</strong> — estas pessoas estão em veículos cuja esteira
                não é da especialidade delas, e não conseguem iniciar atividade ali:
              </p>
              <ul style={{ fontSize: '13px', margin: '4px 0 4px 18px' }}>
                {cobertura.vinculosInconsistentes.map((item) => (
                  <li key={`${item.collaboratorId}-${item.carId}`}>
                    {item.collaboratorName ?? 'Colaborador'} — veículo {item.plate} (
                    {item.esteiraNome})
                  </li>
                ))}
              </ul>
              <p style={{ ...textoSecundario, marginBottom: 0 }}>
                Ajuste a especialidade para incluir a esteira, ou peça ao Chefe de Oficina para
                trocar o colaborador do veículo.
              </p>
            </>
          )}
        </div>
      )}

      {/* --- Formulário --- */}
      <div className="admin-card">
        <div className="admin-card-title">
          {editandoId ? 'Editar Especialidade' : 'Criar Nova Especialidade'}
        </div>
        <p style={{ ...textoSecundario, marginTop: 0 }}>
          A especialidade define em quais esteiras cada colaborador pode trabalhar. Um colaborador
          pode ter mais de uma — nesse caso ele atende a soma das esteiras.
        </p>

        <div className="admin-form-grid">
          <div className="admin-input-group">
            <label className="admin-label" htmlFor="especialidade-nome">
              Nome
            </label>
            <input
              id="especialidade-nome"
              type="text"
              value={nome}
              onChange={(e) => setNome(e.target.value)}
              placeholder="Ex: Pintor"
              maxLength={60}
              className="admin-input"
            />
          </div>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label className="admin-label">Esteiras em que atua</label>
          {esteirasSelecionaveis.length === 0 ? (
            <p style={{ ...textoSecundario, marginTop: '4px' }}>
              Nenhuma esteira de trabalho ativa cadastrada.
            </p>
          ) : (
            <div className="admin-checkbox-group">
              {esteirasSelecionaveis.map((esteira: any) => (
                <label className="admin-checkbox-label" key={esteira.id}>
                  <input
                    type="checkbox"
                    checked={esteiraIds.includes(esteira.id)}
                    onChange={() => alternarEsteira(esteira.id)}
                  />
                  {esteira.nome}
                </label>
              ))}
            </div>
          )}
          <p style={{ ...textoSecundario, marginTop: '4px' }}>
            As etapas do próprio sistema (Aguardando, Orçamento, Entrega) não aparecem aqui: elas não
            dependem de especialidade.
          </p>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label className="admin-label">Colaboradores</label>
          <div style={{ marginTop: '4px' }}>
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={() => {
                setSelecaoNoModal(colaboradores);
                setIsColaboradoresModalOpen(true);
              }}
            >
              Selecionar colaboradores
            </button>
          </div>
          <p style={{ ...textoSecundario, marginTop: '4px' }}>
            {colaboradores.length === 0
              ? 'Nenhum colaborador selecionado — a especialidade fica cadastrada, mas sem ninguém.'
              : colaboradores.map((c) => c.fullName ?? c.id).join(', ')}
          </p>
        </div>

        <div style={{ marginTop: '16px' }}>
          <label className="admin-checkbox-label">
            <input type="checkbox" checked={ativa} onChange={(e) => setAtiva(e.target.checked)} />
            Ativa
          </label>
          <p style={{ ...textoSecundario, marginTop: '4px' }}>
            Especialidade desativada guarda a configuração, mas sai da regra.
          </p>
        </div>

        <div style={{ marginTop: '12px', display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
          <button
            type="button"
            className="admin-btn admin-btn-primary"
            disabled={salvando || cargaFalhou}
            onClick={() => void salvar()}
          >
            {editandoId ? 'Salvar alterações' : 'Criar especialidade'}
          </button>
          {editandoId && (
            <button
              type="button"
              className="admin-btn admin-btn-secondary"
              onClick={limparFormulario}
            >
              Cancelar
            </button>
          )}
        </div>
      </div>

      {/* --- Lista --- */}
      <div className="admin-card">
        <div className="admin-card-title">Especialidades cadastradas</div>

        {carregando ? (
          <p style={{ ...textoSecundario, marginTop: 0 }}>Carregando...</p>
        ) : especialidades.length === 0 ? (
          <p style={{ ...textoSecundario, marginTop: 0 }}>
            {cargaFalhou
              ? 'A lista não pôde ser carregada.'
              : 'Nenhuma especialidade cadastrada ainda.'}
          </p>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table className="admin-table" style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr style={{ textAlign: 'left', borderBottom: '1px solid var(--tema-linha, #e5e7eb)' }}>
                  <th style={{ padding: '8px' }}>Nome</th>
                  <th style={{ padding: '8px' }}>Esteiras</th>
                  <th style={{ padding: '8px' }}>Colaboradores</th>
                  <th style={{ padding: '8px' }}>Situação</th>
                  <th style={{ padding: '8px' }}>Ações</th>
                </tr>
              </thead>
              <tbody>
                {especialidades.map((especialidade) => (
                  <tr key={especialidade.id} style={{ borderBottom: '1px solid var(--tema-linha, #f3f4f6)' }}>
                    <td style={{ padding: '8px' }}>{especialidade.nome}</td>
                    <td style={{ padding: '8px' }}>
                      {especialidade.esteiras.length === 0
                        ? 'Nenhuma'
                        : especialidade.esteiras.map((e) => e.nome).join(', ')}
                    </td>
                    <td style={{ padding: '8px' }}>
                      {especialidade.colaboradores.length === 0
                        ? 'Nenhum'
                        : especialidade.colaboradores.map((c) => c.nome).join(', ')}
                    </td>
                    <td style={{ padding: '8px' }}>
                      {especialidade.ativa ? (
                        'Ativa'
                      ) : (
                        <span className="admin-badge-inactive">Inativa</span>
                      )}
                    </td>
                    <td style={{ padding: '8px', display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        style={{ padding: '2px 6px', fontSize: '11px' }}
                        onClick={() => editar(especialidade)}
                      >
                        Editar
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-secondary"
                        style={{ padding: '2px 6px', fontSize: '11px' }}
                        onClick={() => void alternarAtiva(especialidade)}
                      >
                        {especialidade.ativa ? 'Desativar' : 'Reativar'}
                      </button>
                      <button
                        type="button"
                        className="admin-btn admin-btn-danger"
                        style={{ padding: '2px 6px', fontSize: '11px' }}
                        onClick={() => setParaExcluir(especialidade)}
                      >
                        Excluir
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      <CollaboratorModal
        isOpen={isColaboradoresModalOpen}
        collaborators={colaboradoresPagina}
        selectedUsers={selecaoNoModal}
        setSelectedUsers={setSelecaoNoModal}
        vinculadosAtuais={colaboradores}
        totalCollaborators={totalColaboradores}
        loadPage={carregarPaginaDeColaboradores}
        isSidebarOpen={false}
        onConfirm={() => {
          setColaboradores(selecaoNoModal);
          setIsColaboradoresModalOpen(false);
        }}
        onCancel={() => setIsColaboradoresModalOpen(false)}
        textos={{
          titulo: 'Colaboradores da especialidade',
          atuaisPrefixo: 'Atualmente na especialidade',
          atuaisVazio: 'Nenhum colaborador nesta especialidade',
          badgeAtual: 'Na especialidade',
          // at-93: a linha "não atribuir" do topo da lista fala de VEÍCULO por padrão — aqui o
          // conjunto é a especialidade, e o texto tem de dizer isso.
          semNinguemRotulo: 'Nenhum colaborador',
          semNinguemDescricao: 'A especialidade fica sem colaborador ao salvar',
          avisoRemocao: (quantidade) =>
            `${quantidade === 1 ? '1 colaborador sairá' : `${quantidade} colaboradores sairão`} desta especialidade ao salvar.`,
        }}
      />

      <ConfirmModal
        isOpen={!!paraExcluir}
        title="Excluir especialidade"
        message={
          paraExcluir
            ? `A especialidade "${paraExcluir.nome}" será excluída. Os colaboradores dela deixam de ` +
              'atender as esteiras que só ela cobria. Deseja continuar?'
            : ''
        }
        confirmLabel="Excluir"
        onConfirm={() => paraExcluir && excluir(paraExcluir)}
        onCancel={() => setParaExcluir(null)}
      />

      <ConfirmModal
        isOpen={!!confirmacaoImpacto}
        title="Esta alteração afeta trabalho em andamento"
        message={confirmacaoImpacto?.mensagem ?? ''}
        confirmLabel="Confirmar"
        onConfirm={() => confirmacaoImpacto?.onConfirm()}
        onCancel={() => setConfirmacaoImpacto(null)}
      />

      <ComoFunciona
        isOpen={isHelpOpen}
        onClose={() => setIsHelpOpen(false)}
        titulo="Como Funciona - Especialidades"
        secoes={helpSections}
      />
    </>
  );
};

export default EspecialidadesSection;
